import {
  Button,
  Column,
  Container,
  Copy,
  Flex,
  Loader,
  Row,
  Section,
  Square,
  Squish,
  Stack,
  SvgArrowLeft,
  SvgArrowRight,
  SvgCaretLeft,
  SvgMagnifyingGlass,
  SvgX,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@grubhubprod/gh-cookbook-react/dist';
import {
  IEventData,
  publish,
} from '@grubhubprod/umami-library/dist/utilities/eventManager';
import classnames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form/dist/index.ie11';
import { useHistory, useParams } from 'react-router-dom';
import { Checkbox } from 'src/components/Checkbox/Checkbox';
import { Input } from 'src/components/Input/Input';
import AddPeopleToLocModal from 'src/components/selfService/features/LineOfCredit/details/AddPeopleToLocModal';
import {
  LocModalStatus,
  LineOfCreditAnalyticsEventLabelMap,
  getLineOfCreditAsyncTasksMessagesMap,
} from 'src/components/selfService/features/LineOfCredit/LineOfCredit.utils';
import { LineOfCreditModal } from 'src/components/selfService/features/LineOfCredit/LineOfCreditModal';
import { SimpleDialog } from 'src/components/SimpleDialog/SimpleDialog';
import { SnackbarStatus } from 'src/components/snackbar/SnackbarLauncher';
import { NoResultsMsg } from 'src/components/Table/NoResultsMsg';
import Table from 'src/components/Table/Table';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import {
  ILineOfCreditPayload,
  ILineOfCreditResponseType,
  ILocAddRemoveDiners,
  LineOfCreditAsyncTasks,
} from 'src/types/LineOfCredit';
import { sdk } from 'src/utilities/sdk';
import { AsyncTaskStatus, useAsyncTask } from 'src/utilities/useAsyncTask';
import { useDebouncedValue } from 'src/utilities/useDebouncedValue';
import { useScreenSizes } from 'src/utilities/useScreenSizes';
import {
  LONG_SEARCH_DELAY,
  SearchStatus,
  useLocDinersSearch,
} from 'src/utilities/useSearchHooks';
import { pushErrorFromAPI } from 'src/utilities/notifications';
import { AnalyticsEventLabel } from 'src/types/Analytics';
import {
  ISelfServiceTealiumPageView,
  useAnalytics,
} from 'src/components/AnalyticsProvider/AnalyticsProvider';
import { LineOfCreditDetailsCard } from 'src/components/selfService/features/LineOfCredit/details/LineOfCreditDetailsCard';
import { useSendAnalytics } from 'src/components/AnalyticsProvider/useSendAnalytics';
import { RouterLink } from 'src/components/RouterLink/RouterLink';
import { useMealCreditEntityName } from 'src/components/selfService/common/hooks/useMealCreditEntityName';
import GroupLocTable from './Groups/GroupLocTable';

const FIXED_BODY_HEIGHT = '300px';
const DEBOUNCE_SEARCH_TIMING = 200;

export enum LocPeopleAction {
  ADD,
  REMOVE,
}

enum TabIndex {
  PEOPLE = 0,
  GROUPS = 1,
}

const gaPageEventByTab: Record<TabIndex, ISelfServiceTealiumPageView> = {
  [TabIndex.PEOPLE]: {
    pageGroup: 'corporate self service - account management',
    pageName: 'Line of Credit - Details',
  },
  [TabIndex.GROUPS]: {
    pageGroup: 'corporate self service - account management',
    pageName: 'Grubhub | Self-Service Account | Line of credit | Group List',
    subGroup: 'Line of Credit - Details > Group List',
  },
};

export interface IRemoveDinerModalStatus {
  showModal: boolean;
  people: number;
  dinerId: string;
}

export const getLocPeopleAsyncTasksMessagesMap = (
  entityNameSingular: string
): Record<string, (isPlural: boolean) => string> => ({
  [`${LocPeopleAction.REMOVE}-${SnackbarStatus.IN_PROGRESS}`]: (
    isPlural: boolean
  ): string =>
    `Removing ${
      isPlural ? 'people' : 'this person'
    } from your ${entityNameSingular}`,
  [`${LocPeopleAction.REMOVE}-${SnackbarStatus.SUCCESS}`]: (
    isPlural: boolean
  ): string =>
    `${
      isPlural ? 'People have' : 'This person has'
    } been removed from your ${entityNameSingular}`,
  [`${LocPeopleAction.REMOVE}-${SnackbarStatus.ERROR}`]: (
    isPlural: boolean
  ): string =>
    `There was a problem removing ${
      isPlural ? 'these people' : 'this person'
    } from your ${entityNameSingular}. Try again. `,

  [`${LocPeopleAction.ADD}-${SnackbarStatus.IN_PROGRESS}`]: (
    isPlural: boolean
  ): string =>
    `Adding ${
      isPlural ? 'people' : 'this person'
    } to your ${entityNameSingular}`,
  [`${LocPeopleAction.ADD}-${SnackbarStatus.SUCCESS}`]: (
    isPlural: boolean
  ): string =>
    `${
      isPlural ? 'People have' : 'This person has'
    } been added to your ${entityNameSingular}`,
  [`${LocPeopleAction.ADD}-${SnackbarStatus.ERROR}`]: (
    isPlural: boolean
  ): string =>
    `There was a problem adding ${
      isPlural ? 'people' : 'this person'
    } to your ${entityNameSingular}. Try again.`,
});

export default function LineOfCreditDetails(): JSX.Element {
  const { isPhone, isDesktop } = useScreenSizes();
  const { corpid, locId } = useParams<{ corpid: string; locId: string }>();
  const { push } = useHistory();
  const { MEAL_CREDIT_ENTITY_NAME } = useMealCreditEntityName();

  const [loc, setLoc] = useState<ILineOfCreditResponseType>(null);
  const [isLocLoading, setIsLocLoading] = useState<boolean>(false);
  const [isSelectionActive, setIsSelectionActive] = useState(false);
  const usingCustomSearch = useRef(false);
  const [isLoading, setIsLoading] = useState(false);

  const lastPeopleAction = useRef(LocPeopleAction.ADD);
  const lastPeopleAddedCount = useRef(0);

  const [locModalStatus, setLocModalStatus] = useState<LocModalStatus>({
    showModal: false,
    locId: null,
  });

  const [locDeactivateModalStatus, setLocDeactivateModalStatus] =
    useState<LocModalStatus>({
      showModal: false,
      locId: null,
    });

  const [peopleRemoveModalStatus, setPeopleRemoveModalStatus] =
    useState<IRemoveDinerModalStatus>({
      showModal: false,
      people: 0,
      dinerId: null,
    });

  const [showAddPeopleModal, setShowAddPeopleModal] = useState(false);
  const { sendTealiumPageView } = useAnalytics();
  const { sendAnalytics, sendAnalyticsBefore } = useSendAnalytics({
    eventcategory: 'self-service management tool_loc',
  });
  const [currentTabIndex, setCurrentTabIndex] = useState<TabIndex>(
    TabIndex.PEOPLE
  );

  useEffect(() => {
    sendTealiumPageView(gaPageEventByTab[currentTabIndex]);

    if (currentTabIndex === TabIndex.GROUPS) {
      sendAnalytics({
        eventaction: 'groups tab_cta',
        eventlabel: 'groups tab on loc page',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTabIndex]);

  const {
    status: asyncTaskStatus,
    startAsyncTask,
    task_id,
    task_type,
  } = useAsyncTask();

  const {
    resultsList: locDiners,
    totalResults,
    searchStatus: locDinersSearchStatus,
    hasMoreResults,
    nextSearchPage,
    doSearch: getLocDiners,
    error,
    totalPages,
  } = useLocDinersSearch(corpid as string, locId as string);

  const { register, setValue, watch } = useForm({
    mode: 'onChange',
    defaultValues: { selectedDiners: {}, selectAll: false },
  });

  const debouncedSearchTerm = useDebouncedValue(
    watch('searchTerm', '') as string,
    DEBOUNCE_SEARCH_TIMING
  );
  const selectedDiners = watch('selectedDiners');
  const selectAll = watch('selectAll', false);

  useEffect(() => {
    const activeSelections = Object.keys(selectedDiners)
      .map((k) => selectedDiners[k])
      .some((v) => !!v);

    setIsSelectionActive(activeSelections);
  }, [selectedDiners]);

  useEffect(() => {
    setAllCheckboxState(selectAll as boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectAll]);

  useEffect(() => {
    if (corpid && locId) {
      getLineOfCredit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpid, locId]);

  useEffect(() => {
    if (corpid && locId) {
      if (debouncedSearchTerm) {
        resetLocsSelections();
        usingCustomSearch.current = true;
        getLocDiners(
          { query: debouncedSearchTerm, include_diners_not_in_tag: false },
          false
        );
      } else {
        getLocDiners({ include_diners_not_in_tag: false }, false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpid, locId, debouncedSearchTerm]);

  useEffect(() => {
    if (corpid && asyncTaskStatus === AsyncTaskStatus.COMPLETE) {
      if (task_type === LineOfCreditAsyncTasks.UPDATE_BUDGET_BASIC_ACCOUNT) {
        getLineOfCredit();
        closeEditLocModal();
      }

      if (
        task_type === LineOfCreditAsyncTasks.DEACTIVATE_BUDGET_BASIC_ACCOUNT
      ) {
        // Get back to LOCs list after deleting the LOC
        push(`/corporate/management/${corpid}/lineOfCredit`);
      }

      if (
        task_type === LineOfCreditAsyncTasks.UPDATE_GROUP_DINER_ASSOCIATIONS
      ) {
        if (lastPeopleAction.current === LocPeopleAction.ADD) {
          sendAddPeopleAnalytics();
        }
        setTimeout(() => {
          paginate(1); // refreshes the list
        }, LONG_SEARCH_DELAY);
      }

      sendAnalyticsEvent(task_type, AnalyticsEventLabel.SUCCESS);
      pushSnackbarNotification(task_id, task_type, SnackbarStatus.SUCCESS);
      setIsLoading(false);
    }
    const initialRequestCompletedAndPollingStarted =
      corpid &&
      asyncTaskStatus === AsyncTaskStatus.IN_PROGRESS &&
      task_id &&
      task_type;

    if (initialRequestCompletedAndPollingStarted) {
      pushSnackbarNotification(task_id, task_type, SnackbarStatus.IN_PROGRESS);
    }

    if (corpid && asyncTaskStatus === AsyncTaskStatus.ERROR) {
      sendAnalyticsEvent(task_type, AnalyticsEventLabel.ERROR);
      pushSnackbarNotification(task_id, task_type, SnackbarStatus.ERROR);
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpid, asyncTaskStatus, task_id, task_type]);

  useEffect(() => {
    if (locDinersSearchStatus === SearchStatus.ERROR) {
      pushErrorFromAPI(error, {
        fallbackMessage: `Sorry, there was an issue retrieving diners associated with this ${MEAL_CREDIT_ENTITY_NAME.singular}`,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locDinersSearchStatus]);

  function sendAnalyticsEvent(
    task_type: string,
    eventlabel: AnalyticsEventLabel
  ) {
    sendAnalytics({
      eventaction: LineOfCreditAnalyticsEventLabelMap[task_type],
      eventlabel,
    });
  }

  async function getLineOfCredit() {
    setIsLocLoading(true);
    try {
      const locResponse: ILineOfCreditResponseType = await sdk.http.get(
        `/corps/${corpid}/eventmetadata/${locId}`
      );

      setLoc(locResponse);
    } catch (error) {
      pushErrorFromAPI(error);
    } finally {
      setIsLocLoading(false);
    }
  }

  const handleAddRemoveGroupsSuccess = () => getLineOfCredit();

  function sendAddPeopleAnalytics() {
    sendAnalytics({
      eventaction: 'add people to loc_cta',
      eventlabel: `${lastPeopleAddedCount.current}`,
    });
  }

  async function addPeople(dinersSelected: string[]) {
    lastPeopleAddedCount.current = dinersSelected.length;
    try {
      await startAsyncTask(() => {
        return sdk.http.put(`/corp-admin/${corpid}/groups/v2/${locId}/diners`, {
          diners_to_add: dinersSelected,
          diners_to_remove: [],
        } as ILocAddRemoveDiners);
      });
      lastPeopleAction.current = LocPeopleAction.ADD;
      setShowAddPeopleModal(false);
    } catch (error) {
      pushErrorFromAPI(error);
    }
  }

  async function removePeople(dinerId: string) {
    try {
      const peopleToRemove = dinerId
        ? [dinerId]
        : getPeopleSelectedForRemoval();
      await startAsyncTask(() => {
        return sdk.http.put(`/corp-admin/${corpid}/groups/v2/${locId}/diners`, {
          diners_to_add: [],
          diners_to_remove: peopleToRemove,
        } as ILocAddRemoveDiners);
      });
      lastPeopleAction.current = LocPeopleAction.REMOVE;
      closePeopleRemovalConfirmDialog();
    } catch (error) {
      pushErrorFromAPI(error);
    }
  }

  function getPeopleSelectedForRemoval(): string[] {
    return Object.keys(selectedDiners).reduce((idList, key) => {
      if (selectedDiners[key]) idList.push(key);
      return idList;
    }, []);
  }

  async function onEditFormSubmit(
    budgetPayload: ILineOfCreditPayload
  ): Promise<void> {
    setIsLoading(true);
    try {
      await startAsyncTask(() => {
        return sdk.http.put(
          `/corp-admin/${corpid}/budget/${locModalStatus.locId}`,
          budgetPayload
        );
      });
    } catch (error) {
      setIsLoading(false);
      pushErrorFromAPI(error);
    }
  }

  async function locDeactivationConfirmation(locId: string) {
    closeDeactivateConfirmDialog();

    try {
      await startAsyncTask(() => {
        return sdk.http.delete(`/corp-admin/${corpid}/budget`, {
          budget_ids: [locId],
        });
      });
    } catch (error) {
      pushErrorFromAPI(error);
    }
  }

  function openEditLocModal() {
    resetLocsSelections();
    setLocModalStatus({
      showModal: true,
      locId: loc.id,
    });
  }

  function closeEditLocModal(): void {
    setLocModalStatus({
      showModal: false,
      locId: null,
    });
  }

  function openDeactivateConfirmDialog() {
    resetLocsSelections();
    setLocDeactivateModalStatus({
      showModal: true,
      locId: loc.id,
    });
  }

  function closeDeactivateConfirmDialog(): void {
    setLocDeactivateModalStatus({
      showModal: false,
      locId: null,
    });
  }

  function pushSnackbarNotification(
    task_id: string,
    task_type: string,
    status: SnackbarStatus
  ) {
    let message: string;
    // need to resolve the msg with the people count for removal
    if (task_type === LineOfCreditAsyncTasks.UPDATE_GROUP_DINER_ASSOCIATIONS) {
      const isPlural =
        lastPeopleAction.current === LocPeopleAction.REMOVE
          ? getPeopleSelectedForRemoval().length > 1
          : lastPeopleAddedCount.current > 1;

      message = getLocPeopleAsyncTasksMessagesMap(
        MEAL_CREDIT_ENTITY_NAME.singular
      )[`${lastPeopleAction.current}-${status}`](isPlural);
    } else {
      message = getLineOfCreditAsyncTasksMessagesMap(
        MEAL_CREDIT_ENTITY_NAME.singular
      )[`${task_type}-${status}`];
    }

    publish('push:snackbar', {
      id: task_id,
      status,
      message,
    } as unknown as IEventData);
  }

  function paginate(page: number) {
    usingCustomSearch.current = true;
    getLocDiners({ page }, false);
    resetLocsSelections();
  }

  function setAllCheckboxState(selected: boolean) {
    Object.keys(selectedDiners).forEach((dinerId) => {
      setValue(`selectedDiners[${dinerId}]`, selected);
    });
  }

  function openPeopleRemovalConfirmDialog(people: number, dinerId: string) {
    setPeopleRemoveModalStatus({
      showModal: true,
      people,
      dinerId,
    });
  }
  function closePeopleRemovalConfirmDialog() {
    setPeopleRemoveModalStatus({
      showModal: false,
      people: 0,
      dinerId: null,
    });
  }

  function openAddPeopleModal() {
    resetLocsSelections();
    setShowAddPeopleModal(true);
  }

  function resetLocsSelections() {
    setValue('selectAll', false);
    setAllCheckboxState(false);
  }

  function renderPeopleLocTable() {
    const unassignButtonText = 'Unassign';

    return (
      <Table
        hideOutsideBorder
        isLoading={locDinersSearchStatus === SearchStatus.LOADING}
        hideHeader={false}
        bodyMinHeight={FIXED_BODY_HEIGHT}
        bodyMaxHeight={isDesktop ? FIXED_BODY_HEIGHT : 'auto'}
        headerInsets={isDesktop ? 'lg' : 'md'}
        header={() => (
          <>
            <Stack y="sm">
              <Copy variant="body" color="secondaryText">
                {`People added to this ${MEAL_CREDIT_ENTITY_NAME.singular} will receive an email the
                day it becomes active, with a prompt to start ordering`}
              </Copy>
            </Stack>
            <Stack y="sm">
              <Flex
                flexDirection={isPhone ? 'column' : 'row'}
                justifyContent="space-between"
              >
                <Flex flexBasis={!isPhone ? '248px' : 'auto'}>
                  <Stack
                    y={isPhone ? 'md' : 'none'}
                    x={!isPhone ? 'md' : 'none'}
                    width="100%"
                  >
                    <Input
                      key="searchTerm"
                      ariaLabel="Search by name or email"
                      placeholder="Search by name or email"
                      name="searchTerm"
                      id="searchTerm"
                      prefixIcon={
                        <Copy variant="body" color="secondaryText">
                          <SvgMagnifyingGlass size="16px" />
                        </Copy>
                      }
                      ref={register}
                    />
                  </Stack>
                </Flex>

                <Button
                  variant="primary"
                  buttonSize="medium"
                  onClick={() =>
                    sendAnalyticsBefore(openAddPeopleModal, {
                      eventaction: 'add people to loc_impression',
                      non_interaction: '1',
                    })
                  }
                >
                  Assign people
                </Button>
              </Flex>
            </Stack>
            <Flex>
              <Checkbox
                cancelLabelBottomMargin
                id="selectAll"
                name="selectAll"
                isMultipleSelection
                ariaLabel="select all diners"
                ref={register}
              />

              <Row className="lineOfCreditDetails_checkboxRow">
                {isDesktop ? (
                  <>
                    <Column gutter={24} desktop={3}>
                      <Copy variant="body" color="secondaryText">
                        Name
                      </Copy>
                    </Column>
                    <Column desktop={6}>
                      <Copy variant="body" color="secondaryText">
                        Email
                      </Copy>
                    </Column>
                  </>
                ) : (
                  <Column gutter={24} phone={6}>
                    <Copy variant="body" color="secondaryText">
                      Person details
                    </Copy>
                  </Column>
                )}

                <Column desktop={3} phone={6}>
                  <Flex
                    alignItems="center"
                    justifyContent="flex-end"
                    width="100%"
                  >
                    <Stack x="x-sm">
                      <Copy variant="body" color="secondaryText">
                        {`${locDiners.length} of ${totalResults}`}
                      </Copy>
                    </Stack>

                    {totalPages > 1 && (
                      <>
                        <Stack x="x-sm">
                          <Copy
                            variant="body"
                            color={
                              nextSearchPage > 1
                                ? 'interactive'
                                : 'secondaryText'
                            }
                          >
                            <SvgArrowLeft
                              title="back arrow"
                              className={classnames({
                                lineOfCreditDetails_disabledArrow:
                                  nextSearchPage === 1,
                                lineOfCreditDetails_enabledArrow:
                                  nextSearchPage > 1,
                              })}
                              onClick={() => paginate(nextSearchPage - 1)}
                              size="24px"
                            />
                          </Copy>
                        </Stack>
                        <Copy
                          variant="body"
                          color={
                            hasMoreResults ? 'interactive' : 'secondaryText'
                          }
                        >
                          <SvgArrowRight
                            title="forward arrow"
                            className={classnames({
                              lineOfCreditDetails_disabledArrow:
                                !hasMoreResults,
                              lineOfCreditDetails_enabledArrow: hasMoreResults,
                            })}
                            onClick={() => paginate(nextSearchPage + 1)}
                            size="24px"
                          />
                        </Copy>
                      </>
                    )}
                  </Flex>
                </Column>
              </Row>
            </Flex>
          </>
        )}
        body={() => (
          <>
            {locDiners.length > 0 && (
              <Stack as="ul" className="removeListStyles">
                {locDiners.map((diner) => (
                  <Stack as="div" key={diner.id}>
                    {isDesktop ? (
                      <Squish
                        as="li"
                        borderBottom="1px solid"
                        borderColor="lightLine"
                        padding="12px 24px"
                      >
                        <Flex alignItems="center">
                          <Checkbox
                            cancelLabelBottomMargin
                            id={`selectedDiners[${diner.id}]`}
                            name={`selectedDiners[${diner.id}]`}
                            ariaLabel={`select diner ${diner.name}`}
                            ref={register}
                          />
                          <Row className="lineOfCreditDetails_checkboxRow">
                            <Column gutter={24} desktop={3}>
                              <Flex alignItems="center" height="100%">
                                <Stack as="div" className="ellipsisOverflow">
                                  <Copy variant="body">{diner.name}</Copy>
                                </Stack>
                              </Flex>
                            </Column>
                            <Column desktop={6}>
                              <Flex alignItems="center" height="100%">
                                <Stack as="div" className="ellipsisOverflow">
                                  <Copy variant="body">{diner.email}</Copy>
                                </Stack>
                              </Flex>
                            </Column>

                            <Column desktop={3}>
                              <Flex
                                justifyContent="flex-end"
                                alignItems="center"
                              >
                                <Tooltip
                                  roundedContainer
                                  content={
                                    <Copy
                                      variant="body"
                                      color="invertedPrimaryText"
                                    >
                                      {unassignButtonText}
                                    </Copy>
                                  }
                                >
                                  <Stack as="div">
                                    <Button
                                      variant="tertiary"
                                      buttonSize="medium"
                                      className="removeButtonPadding"
                                      onClick={() =>
                                        openPeopleRemovalConfirmDialog(
                                          1,
                                          diner.id
                                        )
                                      }
                                    >
                                      {unassignButtonText}
                                    </Button>
                                  </Stack>
                                </Tooltip>
                              </Flex>
                            </Column>
                          </Row>
                        </Flex>
                      </Squish>
                    ) : (
                      <Squish
                        inset="md"
                        borderBottom="1px solid"
                        borderColor="lightLine"
                      >
                        <Flex
                          alignItems="flex-start"
                          flexDirection="row"
                          justifyContent="space-between"
                        >
                          <Stack>
                            <Flex alignItems="flex-start">
                              <Stack x="md">
                                <Flex alignItems="center" height="100%">
                                  <Checkbox
                                    cancelLabelBottomMargin
                                    id={`selectedDiners[${diner.id}]`}
                                    name={`selectedDiners[${diner.id}]`}
                                    ariaLabel={`select diner ${diner.name}`}
                                    ref={register}
                                  />
                                </Flex>
                              </Stack>
                              <Flex flexDirection="column">
                                <Square>
                                  <Stack y="xxx-sm">
                                    <Copy variant="body">{diner.name}</Copy>
                                  </Stack>
                                </Square>
                                <Square>
                                  <Copy variant="body">{diner.email}</Copy>
                                </Square>
                              </Flex>
                            </Flex>
                          </Stack>

                          <Stack>
                            <Copy variant="body" color="interactive">
                              <SvgX
                                size="24px"
                                onClick={() =>
                                  openPeopleRemovalConfirmDialog(1, diner.id)
                                }
                              />
                            </Copy>
                          </Stack>
                        </Flex>
                      </Squish>
                    )}
                  </Stack>
                ))}
              </Stack>
            )}
            {locDiners.length === 0 &&
              usingCustomSearch.current &&
              locDinersSearchStatus === SearchStatus.COMPLETE && (
                <NoResultsMsg message="We couldn’t find what you were looking for. Give it another go." />
              )}

            {locDiners.length === 0 &&
              !usingCustomSearch.current &&
              locDinersSearchStatus === SearchStatus.COMPLETE && (
                <NoResultsMsg
                  message={`Start adding people so they can use this ${MEAL_CREDIT_ENTITY_NAME.singular}`}
                />
              )}
          </>
        )}
        justifyFooter="space-between"
        footerMinHeight="auto"
        footerBg="untintedBackground"
        showFooter={isSelectionActive}
        footer={() => (
          <Squish inset="lg" width="100%">
            <Flex
              alignItems="center"
              justifyContent={isPhone ? 'center' : 'space-between'}
              flexDirection={isPhone ? 'column' : 'row'}
            >
              <Stack y={isPhone ? 'x-sm' : 'none'}>
                <Copy variant="lead">{`${
                  getPeopleSelectedForRemoval().length
                } people selected`}</Copy>
              </Stack>
              <Stack width={isPhone ? '100%' : 'auto'}>
                <Button
                  block={isPhone}
                  variant="primary"
                  buttonSize="medium"
                  onClick={() =>
                    openPeopleRemovalConfirmDialog(
                      getPeopleSelectedForRemoval().length,
                      null
                    )
                  }
                >
                  Remove
                </Button>
              </Stack>
            </Flex>
          </Squish>
        )}
      />
    );
  }

  if (!loc) {
    return (
      <>
        {isLocLoading && (
          <Square inset="md" position="relative" minHeight="100vh">
            <Flex
              alignItems="center"
              justifyContent="center"
              position="absolute"
              marginTop="-16px"
              marginLeft="-16px"
              width="100%"
              height="100%"
              zIndex={10}
            >
              <Loader size="lg" />
            </Flex>
          </Square>
        )}
      </>
    );
  }

  return (
    <Container variant="default">
      <Section inset="md">
        <Stack y="md">
          <Flex
            justifyContent="space-between"
            flexDirection={isPhone ? 'column' : 'row'}
          >
            <Stack y={isPhone ? 'x-sm' : 'none'}>
              <Flex alignItems="center">
                <Copy variant="body" color="interactive">
                  <SvgCaretLeft size="16px" />
                </Copy>
                <RouterLink
                  href={`/corporate/management/${corpid}/lineOfCredit`}
                >
                  {`Back to ${MEAL_CREDIT_ENTITY_NAME.plural}`}
                </RouterLink>
              </Flex>
            </Stack>
          </Flex>
        </Stack>
        <LineOfCreditDetailsCard
          loc={loc}
          onEditClick={openEditLocModal}
          onDeleteClick={openDeactivateConfirmDialog}
        />
        <Square bg="untintedBackground" dimension="2">
          <Tabs onChange={setCurrentTabIndex}>
            <Flex flexDirection="column">
              <Stack y="md" />
              <Flex>
                <Stack x={!isPhone ? 'lg' : 'md'} />
                <TabList>
                  <Tab as="button">People</Tab>
                  <Tab as="button">Groups</Tab>
                </TabList>
                <Stack x={!isPhone ? 'lg' : 'md'} />
              </Flex>
            </Flex>
            <TabPanels>
              <TabPanel>
                <Stack className="cancelWhiteSpaceNoWrap">
                  <Square position="relative">{renderPeopleLocTable()}</Square>
                </Stack>
              </TabPanel>
              <TabPanel>
                {!!loc && (
                  <GroupLocTable
                    loc={loc}
                    corpId={corpid as string}
                    onAddGroupsSuccess={handleAddRemoveGroupsSuccess}
                    onRemoveGroupsSuccess={handleAddRemoveGroupsSuccess}
                  />
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Square>
      </Section>

      {locModalStatus.showModal && (
        <LineOfCreditModal
          isLoading={isLoading}
          loc={loc}
          locId={locModalStatus.locId}
          corpId={corpid as string}
          closeModal={closeEditLocModal}
          onSubmit={onEditFormSubmit}
        />
      )}

      {locDeactivateModalStatus.showModal && (
        <SimpleDialog
          id={locDeactivateModalStatus.locId}
          primaryActionFlavor="danger"
          title={`Delete this ${MEAL_CREDIT_ENTITY_NAME.singular}?`}
          description={`After you delete this ${MEAL_CREDIT_ENTITY_NAME.singular}, people won’t be able to use it. `}
          primaryActionText="Delete"
          buttonsDirection="row-reverse"
          secondaryActionText="Cancel"
          onPrimaryActionClick={(id) => locDeactivationConfirmation(id)}
          onSecondaryActionClick={closeDeactivateConfirmDialog}
        />
      )}

      {peopleRemoveModalStatus.showModal && (
        <SimpleDialog
          primaryActionFlavor="danger"
          title={`${
            peopleRemoveModalStatus.people === 1
              ? 'Remove this person?'
              : 'Remove people?'
          } `}
          description={`Once they are removed, they won’t be able to use this ${MEAL_CREDIT_ENTITY_NAME.singular}.`}
          primaryActionText="Remove"
          buttonsDirection="row-reverse"
          secondaryActionText="Cancel"
          onPrimaryActionClick={() =>
            removePeople(peopleRemoveModalStatus.dinerId)
          }
          onSecondaryActionClick={closePeopleRemovalConfirmDialog}
        />
      )}

      {showAddPeopleModal && (
        <AddPeopleToLocModal
          headerAndButtonText="Assign people"
          onAdd={addPeople}
          onClose={() => setShowAddPeopleModal(false)}
          locId={locId as string}
        />
      )}
    </Container>
  );
}
